import { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { faBookOpenCover } from "@fortawesome/pro-solid-svg-icons/faBookOpenCover";
import { faCompassDrafting } from "@fortawesome/pro-solid-svg-icons/faCompassDrafting";
import { faGraduationCap } from "@fortawesome/pro-solid-svg-icons/faGraduationCap";
import { faHandHoldingHand } from "@fortawesome/pro-solid-svg-icons/faHandHoldingHand";
import { faIdBadge } from "@fortawesome/pro-solid-svg-icons/faIdBadge";
import { faIndustryWindows } from "@fortawesome/pro-solid-svg-icons/faIndustryWindows";
import { faMemoCircleCheck } from "@fortawesome/pro-solid-svg-icons/faMemoCircleCheck";
import { faPlaneDeparture } from "@fortawesome/pro-solid-svg-icons/faPlaneDeparture";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, CardContent, Container, Grid, Link, Pagination, Typography } from '@mui/material';
import { APP_PAGE_SIZE_DEFAULT } from 'app/appConstants';
import { MprcConfirmDialog } from "components";
import { ButtonAdd } from "components/buttons";
import { MprcFilterCount, MprcFilterLine } from 'components/filter';
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { IconButtonDelete } from "components/iconButtons";
import { IconFilter } from 'components/icons';
import { MprcBarTitle } from 'components/MprcBarTitle';
import { MprcContainer } from 'components/MprcContainer';
import { MprcDropDownItemAny, MprcIconButtonDropdown } from 'components/MprcIconButtonDropdown';
import { MprcRequestErrorDialog } from 'components/MprcRequestErrorDialog';
import { MprcRequestIndicator } from 'components/MprcRequestIndicator';
import { MprcSearchTextField, MprcSearchTextFieldHandle } from "components/MprcSearchTextField";
import { ManagedUserCredentialsResponse, PaginatedUserCredentials, UserCredentialType, UserCredentialTypeSelectorResponse } from 'generated/CoreApi_v1';
import { Formaters } from "lib/Formaters";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from "providers/SessionProvider";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditUserHeader } from '../EditUserHeader';
import { AddOrUpdateCredential } from './AddOrUpdateCredential';

////////////////////////////////////////////////////////////////////////////

interface UserCredentialFilterOptions {
    searchText: string;
    credentialType?: UserCredentialType;
}

interface IProps {
    userId: string;
    userName: string;
    editMode: "default" | "profile";
    onClose: () => void;
}

////////////////////////////////////////////////////////////////////////////

export const EditUserCredentials: React.FC<IProps> = (props) => {

    const { userId, userName, editMode, onClose } = props;

    const api = useCoreApi();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useSession();

    const [loading, setLoading] = useState<boolean>(true);
    const [viewData, setViewData] = useState<PaginatedUserCredentials | null>(null);
    const [viewError, setViewError] = useState<any>(null);
    const [dialog, setDialog] = useState<any>();
    const [deleting, setDeleting] = useState<string | null>(null);

    const credentialTypes = useRef<UserCredentialTypeSelectorResponse[]>([]);
    const pageNumber = useRef<number>(1);
    const filterOptions = useRef<UserCredentialFilterOptions>({
        searchText: "",
        credentialType: undefined
    });

    const searchRef = useRef<MprcSearchTextFieldHandle | null>(null);

    const filtered = filterOptions.current.credentialType != undefined || (filterOptions.current.searchText.length || 0) > 0;

    ////////////////////////////////////////////////////////////////////////////

    ///
    const loadPage = (page: number) => {
        pageNumber.current = page;
        setLoading(true);
    };

    ///
    const handleFilterChange = (filter: UserCredentialFilterOptions) => {
        filterOptions.current = filter;
        loadPage(1);
    };

    ///
    const handleResetFilter = () => {
        filterOptions.current = { searchText: "", credentialType: undefined };
        searchRef.current?.reset();
        loadPage(1);
    };

    ///
    const getCredentialTypes = useCallback((): MprcDropDownItemAny[] => {
        const allTypesOption: MprcDropDownItemAny = { text: t("User.Edit.Credentials.AllTypesAction"), value: undefined };
        return credentialTypes.current == undefined ? [allTypesOption] : [allTypesOption, ...credentialTypes.current.map(i => ({ text: i.name, value: i.type }))];
    }, [credentialTypes]);

    ///
    const handleAddOrUpdate = (record?: ManagedUserCredentialsResponse) => {
        const action = record == undefined ? "create" : "update";

        setDialog(<AddOrUpdateCredential
            userId={userId}
            userName={userName}
            record={record}
            credentialTypes={credentialTypes.current}
            onClose={(newRecord) => {
                if (newRecord != undefined && viewData != undefined) {
                    if (action == "create") {
                        const newItems = [newRecord, ...viewData.items];
                        setViewData({ ...viewData, items: newItems, totalRows: newItems.length });
                    }
                    else {
                        const newItems = viewData.items.map(i => {
                            if (i.credentialId == newRecord.credentialId) {
                                i = newRecord;
                            }
                            return i;
                        });
                        setViewData({ ...viewData, items: newItems });
                    }
                }
                setDialog(null);
            }}
        />
        );
    };

    ///
    const handleDelete = (credentialId: string) => {
        setDialog(<MprcConfirmDialog
            message="Confirma a exclusão desta credencial?"
            open
            severity="danger"
            onClose={yes => {
                if (yes) {
                    setDeleting(credentialId);
                }
                setDialog(null);
            }}
        />);
    };

    ///
    const getIcon = useCallback((credentialType: UserCredentialType): IconDefinition => {
        let icon;
        switch (credentialType) {
            case UserCredentialType.PositionsHeld:
                icon = faIdBadge;
                break;
            case UserCredentialType.ProfessionalExperience:
                icon = faIndustryWindows;
                break;
            case UserCredentialType.WorkProjects:
                icon = faCompassDrafting;
                break;
            case UserCredentialType.AcademicBackground:
                icon = faGraduationCap;
                break;
            case UserCredentialType.Research:
                icon = faBookOpenCover;
                break;
            case UserCredentialType.PaperOrPublication:
                icon = faMemoCircleCheck;
                break;
            case UserCredentialType.InternationalActivity:
                icon = faPlaneDeparture;
                break;
            case UserCredentialType.VolunteerWork:
                icon = faHandHoldingHand;
                break;
        }

        return icon;
    }, []);

    ///
    const getPeriod = useCallback((record: ManagedUserCredentialsResponse): string => {

        const startedOn = record.startedOn ? moment(record.startedOn) : undefined;
        const finishedOn = record.finishedOn ? moment(record.finishedOn) : undefined;

        if (startedOn != undefined && finishedOn != undefined) {
            if (startedOn.month() == 0 && finishedOn.month() == 11) {

                if (startedOn.year() === finishedOn.year()) {
                    return startedOn.year().toString();
                }
                else {
                    return `${startedOn.year().toString()} - ${finishedOn.year().toString()}`;
                }
            }

            return `${Formaters.monthYear(startedOn.toDate(), false)} - ${Formaters.monthYear(finishedOn.toDate(), false)}`;
        }
        else if (startedOn != undefined) {
            return `${Formaters.monthYear(startedOn.toDate(), false)} - ${t("User.Edit.Credentials.CurrentlyText")}`;
        }
        else if (finishedOn != undefined) {
            return `${t("User.Edit.Credentials.UntilText")} ${Formaters.monthYear(finishedOn.toDate(), false)}`;
        }

        return t("User.Edit.Credentials.UnspecifiedPeriodText");
    }, []);

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////////

    // Ao remover...
    useEffect(() => {
        async function postData() {

            if (deleting) {
                try {
                    if ((await api.v1.userManagement.deleteCredential({ credentialId: deleting!, userId })).data === true) {
                        enqueueSnackbar(t("User.Edit.Credentials.DeleteSuccessMessage"), { variant: "success" });
                        if (viewData!.items.length == 1 && pageNumber.current > 1) pageNumber.current--;
                        setLoading(true);
                    }
                    else
                        enqueueSnackbar(t("User.Edit.Credentials.DeleteFailMessage"), { variant: "error" });
                }
                catch (e) {
                    setViewError(e);
                }
            }

            setDeleting(null);
        }

        postData();

    }, [deleting]);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {

        if (!loading) return;

        const promises: any[] = [
            api.v1.userManagement.getCredentials({
                userId,
                searchText: filterOptions.current.searchText ?? null,
                pageNumber: pageNumber.current,
                pageSize: APP_PAGE_SIZE_DEFAULT,
                credentialType: filterOptions.current.credentialType ?? null
            }),
        ];

        if (credentialTypes.current.length == 0)
            promises.push(api.v1.selectors.userCredentialTypes());


        Promise.all(promises)
            .then(results => {

                const result = results[0];
                setViewData(result.data);

                if (results.hasOwnProperty(1)) {
                    const resultCredentialTypes = results[1];
                    credentialTypes.current = resultCredentialTypes.data;
                }
            }).catch(e => {
                setViewData(null);
                setViewError(e as Error);
            })
            .finally(() => setLoading(false));

    }, [loading]);


    ///
    const RenderFilter = (
        <MprcCardFilterBox>
            <MprcFilterLine>
                {!viewData?.canUpdateCredentials
                    ? <Typography variant="h3" >{t("User.Edit.Credentials.Title")}</Typography>
                    : <ButtonAdd size="large" color="inherit" onClick={() => handleAddOrUpdate(undefined)} text={t("User.Edit.Credentials.AddButtonText")} />
                }
                <MprcIconButtonDropdown
                    icon={<IconFilter />}
                    onChange={(type) => handleFilterChange({ ...filterOptions.current, credentialType: type })}
                    items={getCredentialTypes()}
                    selected={filterOptions.current.credentialType}
                    menuItemCheck
                    color={filterOptions.current.credentialType != undefined ? "warning" : "muted"}
                />
            </MprcFilterLine>

            <MprcSearchTextField
                placeholder={t("User.Edit.Credentials.SearchTextPlaceholder")}
                onConfirm={(value) => {
                    filterOptions.current = { ...filterOptions.current, searchText: value };
                    loadPage(1);
                }}
                confirmMode="onEnter"
                ref={searchRef}
            />
        </MprcCardFilterBox>
    );

    const RenderGrid =
        <>
            <Grid container spacing={2} sx={{ position: "relative" }} direction="column">
                {
                    viewData?.items.map((item) =>
                        <Grid key={item.credentialId}>
                            <Grid container spacing={1} alignItems="center">
                                <Grid>
                                    <FontAwesomeIcon icon={getIcon(item.credentialType)} size="3x" fixedWidth />
                                </Grid>
                                <Grid size="grow">
                                    <Grid container direction="column">
                                        <Grid>
                                            {viewData.canUpdateCredentials ?
                                                <Link onClick={() => handleAddOrUpdate(item)}>
                                                    <Typography variant="h5">{item.title}</Typography>
                                                </Link>
                                                :
                                                <Typography variant="h5">{item.title}</Typography>
                                            }
                                        </Grid>
                                        <Grid>
                                            <Typography variant="caption"><strong>{getPeriod(item)}</strong></Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {viewData.canUpdateCredentials &&
                                    <Grid>
                                        <IconButtonDelete size="small" onClick={() => handleDelete(item.credentialId)} />
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                    )
                }
            </Grid>
        </>;

    const RenderContent =
        <>
            <Container maxWidth="lg">
                <Card sx={{ p: 0, mt: 0 }}>
                    {RenderFilter}
                    <CardContent>
                        <MprcContainer>
                            <MprcFilterCount
                                loading={loading}
                                isEmpty={(viewData?.totalRows ?? 0) == 0}
                                countText={t("User.Edit.Credentials.RecordsFoundText", { count: viewData?.totalRows ?? 0 })}
                                filter={{
                                    active: filtered,
                                    text: t("User.Edit.Credentials.RecordsFilteredText", { count: viewData?.totalRows ?? 0 }),
                                    onResetFilter: handleResetFilter
                                }}
                            />
                        </MprcContainer>
                        <Grid container direction="column">
                            <Grid>
                                {
                                    viewData && viewData.totalRows > 0 &&
                                    <MprcContainer>
                                        {RenderGrid}
                                    </MprcContainer>
                                }
                            </Grid>
                            <Grid alignSelf="center">
                                {
                                    viewData && viewData.totalPages > 1 &&
                                    <Pagination
                                        shape="rounded"
                                        color="primary"
                                        page={viewData.pageNumber}
                                        count={viewData.totalPages}
                                        onChange={(e, page) => loadPage(page)}
                                    />
                                }
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Container>
        </>;

    return (
        <>
            <MprcRequestIndicator
                visible={loading || deleting != undefined}
                type={loading ? "request" : "post"}
            />

            <MprcRequestErrorDialog
                error={viewError}
                onClose={() => { setViewError(undefined); }}
            />
            {dialog}

            <Grid container direction="column" flexWrap="nowrap" spacing={2} height="100%">
                <Grid>
                    <EditUserHeader
                        onClose={onClose}
                        recordChanged={false}
                        reloadOnClose={false}
                        actions={[
                            <MprcBarTitle>
                                {editMode == "profile" && userId == user.userId ? t("User.Common.YourProfileText") : userName}
                            </MprcBarTitle>
                        ]}
                    />
                </Grid>
                <Grid size="grow">
                    {RenderContent}
                </Grid>
            </Grid>
        </>
    );
};
